import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { MachineDocumentState } from "../../models/machineDocumentsModel";
import { getDocuments, selectDocument } from "../../customerMachineDocumentsSlice";
import FullHeight from "../../../../shared/components/FullHeight";
import Loader from "../../../../shared/components/Loader";
import colors from "../../../../shared/themes/colors";
import { Box, Button } from "@mui/material";
import CreateDocumentDialog from "./CreateDocumentDialog";
import ChangeDocumentDialog from "./ChangeDocumentDialog";
import DocumentItem from "./DocumentItem";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface ManualsAndSoftwareProps {
  machineId: string;
}

const ManualsAndSoftware = ({ machineId }: ManualsAndSoftwareProps) => {
  const { documents, loading, success, error } = useAppSelector<MachineDocumentState>(
    (state: any) => state.customerMachineDocuments
  );

  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");

  // NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents
  // are login-only. Do not re-add a visibility tick or a public docs list without a
  // new owner ruling. The lazy `fetchNameplateStatus` that used to run here existed
  // ONLY to drive the tick's inert state; this tab no longer touches the nameplate.

  const [isCreateDocumentDialog, setIsCreateDocumentDialog] = useState(false);
  const [isChangeDocumentDialog, setIsChangeDocumentDialog] = useState(false);

  const handleCreateDocument = () => {
    dispatch(selectDocument(null));
    setIsCreateDocumentDialog(true);
  };

  const downloadFiles = async () => {
    if (!documents) {
      return;
    }

    const urls = documents.map((x) => x.file?.content);

    for (let url of urls) {
      if (url) {
        let a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", "");
        a.click();
        a.remove();
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  const handleDownloadFiles = () => {
    downloadFiles();
  };

  useEffect(() => {
    dispatch(getDocuments(machineId));
  }, []);

  return (
    <Box className="px-4 h-full">
      {loading ? (
        <FullHeight verticalOffset="medium">
          <Loader />
        </FullHeight>
      ) : !documents?.length ? (
        <FullHeight>
          <Box
            className="text-18-regular"
            color={colors.secondary.gray40}
            sx={{ display: "flex", flexDirection: "column", mt: "100px", alignItems: "center" }}
          >
            <Box>{translate("manualsAndSoftwareEmpty")}</Box>
            <Box className="mt-2">
              <Button variant="contained" onClick={handleCreateDocument}>
                {translate("addItem")}
              </Button>
            </Box>
          </Box>
        </FullHeight>
      ) : (
        <Box className="flex flex-col gap-5 h-full">
          {documents?.map((document, i) => (
            <DocumentItem
              key={i}
              documentFile={document}
              documentIndex={i}
              machineId={machineId}
              onOpenChange={() => setIsChangeDocumentDialog(true)}
            />
          ))}

          {documents && documents?.length > 0 && (
            <Box className="flex justify-end gap-2 mt-2">
              <Button variant="outlined" onClick={handleDownloadFiles}>
                {translate("downloadAllFiles")}
              </Button>
              <Button variant="contained" onClick={handleCreateDocument}>
                {translate("addItem")}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isCreateDocumentDialog && <CreateDocumentDialog
        open={isCreateDocumentDialog}
        onClose={() => setIsCreateDocumentDialog(false)}
        machineId={machineId}
      />}

      <ChangeDocumentDialog
        open={isChangeDocumentDialog}
        onClose={() => setIsChangeDocumentDialog(false)}
        machineId={machineId}
      />
    </Box>
  );
};
export default ManualsAndSoftware;